import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/core/lib/utils';
import { Icon } from './Icon';
import { ICON_POSITION, IconPosition } from '@/core/types/enums';
export const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors w-auto focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:bg-primary-disable disabled:text-disable',
  {
    variants: {
      intent: {
        cta: '',
        destructive: '',
      },
      variant: {
        primary: '',
        secondary: '',
        tertiary: '',
      },
      size: {
        lg: 'h-[48px] px-6 text-base gap-3 rounded-md',
        default: 'h-[40px] px-4 text-sm gap-2 rounded-md',
        sm: 'h-[32px] px-3 text-sm gap-2 rounded-sm',
        xs: 'h-[26px] px-2 text-xs gap-1.5 rounded-sm',
      },
    },
    compoundVariants: [
      // CTA Primary
      {
        intent: 'cta',
        variant: 'primary',
        className:
          'bg-primary-default text-white hover:bg-primary-hover active:bg-primary-pressed disabled:bg-primary-disable disabled:text-disable',
      },
      // CTA Secondary
      {
        intent: 'cta',
        variant: 'secondary',
        className:
          'bg-container-primary text-primary border border-primary-default hover:bg-tertiary-primary-hover active:bg-tertiary-primary-pressed disabled:bg-tertiary-primary-disable disabled:border-stroke-disable disabled:text-disable',
      },
      // CTA Tertiary
      {
        intent: 'cta',
        variant: 'tertiary',
        className:
          'bg-container-primary text-primary hover:bg-tertiary-primary-hover active:bg-tertiary-primary-pressed disabled:bg-tertiary-primary-disable disabled:text-disable',
      },
      // Destructive Primary
      {
        intent: 'destructive',
        variant: 'primary',
        className:
          'bg-error-default text-white hover:bg-error-hover active:bg-error-pressed disabled:bg-error-disable disabled:text-disable',
      },
      // Destructive Secondary
      {
        intent: 'destructive',
        variant: 'secondary',
        className:
          'bg-container-primary text-error-default border border-error-default hover:bg-tertiary-error-hover active:bg-tertiary-error-pressed disabled:bg-tertiary-error-disable disabled:border-stroke-disable disabled:text-disable',
      },
      // Destructive Tertiary
      {
        intent: 'destructive',
        variant: 'tertiary',
        className:
          'bg-container-primary text-error-default hover:bg-tertiary-error-hover active:bg-tertiary-error-pressed disabled:bg-tertiary-error-disable disabled:border-stroke-disable disabled:text-disable',
      },
    ],
    defaultVariants: {
      intent: 'cta',
      variant: 'primary',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>,
    VariantProps<typeof buttonVariants> {
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  iconSize?: number;
  iconPosition?: IconPosition;
  iconColor?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      intent = 'cta',
      variant = 'primary',
      size = 'default',
      icon = null,
      iconSize,
      iconPosition = ICON_POSITION.LEFT,
      iconColor: customIconColor,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    // Determine icon size based on button size
    const getIconSize = () => {
      if (iconSize) return iconSize;
      switch (size) {
        case 'lg':
          return 22;
        case 'default':
          return 20;
        case 'sm':
          return 14;
        case 'xs':
          return 12;
        default:
          return 16;
      }
    };

    // Determine icon color based on variant and state
    const getIconColor = () => {
      // Use custom icon color if provided
      if (customIconColor) return customIconColor;

      if (disabled) return 'var(--icon-disable)';
      if (variant === 'primary') return 'var(--icon-white)';
      if (variant === 'tertiary') {
        // Tertiary buttons use lighter colors for icons
        if (intent === 'cta') return '#FFAC54'; // primary-300
        if (intent === 'destructive') return '#FF7875'; // error-300
      }
      if (intent === 'cta') return 'var(--icon-primary-1)';
      if (intent === 'destructive') return 'var(--icon-error-1)';
      return 'currentColor';
    };

    const iconColor = getIconColor();
    const iconSizeValue = getIconSize();

    const renderIcon = () => {
      if (!icon) return null;
      return <Icon icon={icon} color={iconColor} size={iconSizeValue} />;
    };

    const showLeftIcon =
      icon &&
      (iconPosition === ICON_POSITION.LEFT ||
        iconPosition === ICON_POSITION.BOTH);
    const showRightIcon =
      icon &&
      (iconPosition === ICON_POSITION.RIGHT ||
        iconPosition === ICON_POSITION.BOTH);

    return (
      <button
        className={cn(buttonVariants({ intent, variant, size }), className)}
        ref={ref}
        disabled={disabled}
        data-pte-name='button'
        {...props}
      >
        {showLeftIcon && renderIcon()}
        {children}
        {showRightIcon && renderIcon()}
      </button>
    );
  }
);
Button.displayName = 'Button';
